import { useEffect, useRef, useState } from "react";

const features = [
  {
    icon: "ri-shield-check-line",
    title: "Advanced Security",
    description:
      "Every app we build is engineered with security at its core. From encrypted data transmission to secure local storage, we ensure your users' information is always protected.",
  },
  {
    icon: "ri-speed-line",
    title: "Performance Optimized",
    description:
      "Our apps are meticulously optimized for speed and efficiency. Low memory footprint, fast launch times, and smooth animations — because performance is a feature.",
  },
  {
    icon: "ri-eye-off-line",
    title: "Privacy by Design",
    description:
      "We build with a strict no-data-collection philosophy. Our products are designed to give users full control over their digital privacy without compromise.",
  },
  {
    icon: "ri-smartphone-line",
    title: "Native iOS Experience",
    description:
      "Built exclusively for iOS using native frameworks, our apps integrate seamlessly with the Apple ecosystem — delivering the polished experience users expect.",
  },
  {
    icon: "ri-refresh-line",
    title: "Regular Updates",
    description:
      "We continuously improve our products with regular updates, new features, and compatibility patches — ensuring our apps stay current with every iOS release.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Dedicated Support",
    description:
      "Our team is committed to providing responsive, helpful support. We listen to user feedback and use it to drive meaningful improvements in every release.",
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="features"
      ref={sectionRef}
      className="w-full bg-white py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`mb-16 md:mb-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">What We Offer</p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight max-w-3xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Powerful Features
            <span className="block text-gray-400 font-light">Built for Performance</span>
          </h2>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, i) => (
            <div
              key={feature.title}
              className={`group p-7 md:p-8 rounded-2xl border border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-200 transition-all duration-500 cursor-default ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${100 + i * 80}ms` }}
            >
              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-900 mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className={`${feature.icon} text-white text-xl`} />
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>

              {/* Description */}
              <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Bottom visual strip */}
        <div
          className={`mt-20 rounded-2xl overflow-hidden relative transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/8ff952f5c85d1bee3c0b2bef1f38d8c3.jpg"
            alt="iOS app development showcase"
            className="w-full h-48 md:h-64 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/70 via-gray-900/30 to-transparent flex items-center px-10 md:px-16">
            <div>
              <p className="text-white/60 text-xs tracking-widest uppercase font-medium mb-2">Our Commitment</p>
              <p
                className="text-white text-2xl md:text-3xl font-bold max-w-lg leading-snug"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Every line of code written with purpose and precision.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
